import type { TaskStatus } from "@/lib/tasks/queries";

/** Kanban column display titles from CSP-App Tasks board (Figma 579:8051). */
export const TASK_KANBAN_COLUMN_TITLES: Record<TaskStatus, string> = {
  scheduled: "Today",
  todo: "Backlog",
  in_progress: "In Progress",
  needs_review: "Review",
  done: "Done",
  canceled: "Canceled",
  failed: "Failed",
};

/** Tinted column header backgrounds — one per status lane. */
export const TASK_KANBAN_COLUMN_HEADER_BG: Record<TaskStatus, string> = {
  scheduled: "bg-[#3095DD]/10",
  todo: "bg-[#eee]",
  in_progress: "bg-[#5D60ED]/10",
  needs_review: "bg-[#FFAE51]/10",
  done: "bg-[#22c55e]/10",
  canceled: "bg-[#eee]",
  failed: "bg-[#d30505]/10",
};

const COLUMN_GRADIENT_FADE_LENGTH_PX = 180;

/** Solid / tinted fills used in column headers and gradient starts. */
export const TASK_KANBAN_COLUMN_TINT: Record<TaskStatus, string> = {
  scheduled: "rgb(48 149 221 / 0.1)",
  todo: "#eee",
  in_progress: "rgb(93 96 237 / 0.1)",
  needs_review: "rgb(255 174 81 / 0.1)",
  done: "rgb(34 197 94 / 0.1)",
  canceled: "#eee",
  failed: "rgb(211 5 5 / 0.1)",
};

function columnGradientBelowHeader(tint: string): string {
  return `linear-gradient(to bottom, ${tint} 0px, transparent ${COLUMN_GRADIENT_FADE_LENGTH_PX}px)`;
}

/** Full tint at header bottom, fading downward behind the card stack. */
export const TASK_KANBAN_COLUMN_GRADIENT: Record<TaskStatus, string> = {
  scheduled: columnGradientBelowHeader(TASK_KANBAN_COLUMN_TINT.scheduled),
  todo: columnGradientBelowHeader(TASK_KANBAN_COLUMN_TINT.todo),
  in_progress: columnGradientBelowHeader(TASK_KANBAN_COLUMN_TINT.in_progress),
  needs_review: columnGradientBelowHeader(TASK_KANBAN_COLUMN_TINT.needs_review),
  done: columnGradientBelowHeader(TASK_KANBAN_COLUMN_TINT.done),
  canceled: columnGradientBelowHeader(TASK_KANBAN_COLUMN_TINT.canceled),
  failed: columnGradientBelowHeader(TASK_KANBAN_COLUMN_TINT.failed),
};

/** Fade the column tint into a surface color — for short columns that would
 *  otherwise clip before reaching transparent. */
export function taskKanbanColumnGradientToSurface(
  status: TaskStatus,
  surfaceColor: string,
): string {
  const tint = TASK_KANBAN_COLUMN_TINT[status];
  return `linear-gradient(to bottom, ${tint} 0%, ${tint} 18%, ${surfaceColor} 100%)`;
}

export const TASK_KANBAN_COLUMN_WIDTH_CLASS =
  "w-[340px] shrink-0 grow-0 flex-none";

/** Top corner radius shared by column shell, gradient, and header. */
export const TASK_KANBAN_COLUMN_TOP_RADIUS_CLASS = "rounded-t-lg";

/** Bottom content fade — matches onboarding memory slide (`memory-slide.tsx`). */
export const TASK_KANBAN_COLUMN_BOTTOM_FADE_MASK =
  "linear-gradient(to bottom, black calc(100% - 181px), transparent 100%)";

export const taskKanbanColumnBottomFadeMaskStyle = {
  WebkitMaskImage: TASK_KANBAN_COLUMN_BOTTOM_FADE_MASK,
  maskImage: TASK_KANBAN_COLUMN_BOTTOM_FADE_MASK,
} as const;
